import { useState, useEffect } from "react";
import axios from "axios";
import { Trophy, ChevronDown, ChevronUp } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Leaderboard = () => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeaderboard();
    // Auto-refresh every 30 seconds for "live" updates
    const interval = setInterval(fetchLeaderboard, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/leaderboard`);
      setLeaderboard(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching leaderboard:", error);
      setLoading(false);
    }
  };

  const toggleRow = (index) => {
    setExpandedRows(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#FDFBF7" }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#006747] mx-auto mb-4"></div>
          <p className="text-[#4A5B52]" style={{ fontFamily: "'Figtree', sans-serif" }}>Loading leaderboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#FDFBF7" }}>
      <header className="bg-white/80 backdrop-blur-xl border-b border-[#006747]/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-center space-x-3">
            <div className="w-12 h-12 bg-[#006747] rounded-full flex items-center justify-center">
              <Trophy className="w-6 h-6 text-white" strokeWidth={1.5} />
            </div>
            <div className="text-center">
              <h1 className="text-3xl font-bold text-[#1C2A21]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Live Leaderboard
              </h1>
              <p className="text-sm text-[#8A9A92]" style={{ fontFamily: "'Figtree', sans-serif" }}>
                Masters Pool 2026 • Lowest 4 of 6 Count
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl border border-[#E2E8E4] shadow-sm overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-[#F3F5F1]">
                <th className="px-4 py-4 text-left text-sm font-semibold text-[#1C2A21] uppercase tracking-wider" style={{ fontFamily: "'Figtree', sans-serif" }}>Rank</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-[#1C2A21] uppercase tracking-wider" style={{ fontFamily: "'Figtree', sans-serif" }}>Participant</th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-[#1C2A21] uppercase tracking-wider" style={{ fontFamily: "'Figtree', sans-serif" }}>Total</th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-[#1C2A21] uppercase tracking-wider" style={{ fontFamily: "'Figtree', sans-serif" }}>Tiebreaker</th>
                <th className="px-4 py-4 text-center text-sm font-semibold text-[#1C2A21] uppercase tracking-wider" style={{ fontFamily: "'Figtree', sans-serif" }}></th>
              </tr>
            </thead>
            <tbody>
              {leaderboard.map((entry, index) => (
                <>
                  <tr
                    key={index}
                    className={`border-t border-[#E2E8E4] cursor-pointer hover:bg-[#F3F5F1] ${
                      index === 0 ? "bg-[#F2A900]/5" : ""
                    }`}
                    onClick={() => toggleRow(index)}
                  >
                    <td className="px-4 py-4">
                      <span
                        className={`inline-flex items-center justify-center w-10 h-10 rounded-full font-bold ${
                          index === 0
                            ? "bg-[#F2A900] text-[#1A1F16]"
                            : index === 1
                            ? "bg-gray-300 text-gray-700"
                            : index === 2
                            ? "bg-orange-200 text-orange-800"
                            : "bg-[#F3F5F1] text-[#4A5B52]"
                        }`}
                        style={{ fontFamily: "'Figtree', sans-serif" }}
                      >
                        {index + 1}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-semibold text-[#1C2A21] text-lg" style={{ fontFamily: "'Figtree', sans-serif" }}>
                        {entry.name}
                      </p>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-2xl font-bold text-[#006747]" style={{ fontFamily: "'Figtree', sans-serif" }}>
                        {entry.total_score !== null ? entry.total_score : "-"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center text-[#4A5B52]" style={{ fontFamily: "'Figtree', sans-serif" }}>
                      {entry.tiebreaker !== null && entry.tiebreaker !== undefined ? entry.tiebreaker : "-"}
                    </td>
                    <td className="px-4 py-4 text-center">
                      {expandedRows[index] ? (
                        <ChevronUp className="w-5 h-5 text-[#8A9A92]" strokeWidth={1.5} />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-[#8A9A92]" strokeWidth={1.5} />
                      )}
                    </td>
                  </tr>
                  {expandedRows[index] && (
                    <tr className="border-t border-[#E2E8E4] bg-[#F3F5F1]/30">
                      <td colSpan="5" className="px-8 py-6">
                        <div className="space-y-3">
                          <p className="text-sm font-semibold text-[#1C2A21] uppercase tracking-wider mb-3" style={{ fontFamily: "'Figtree', sans-serif" }}>
                            Player Picks (Lowest 4 of 6 Count)
                          </p>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {entry.player_scores && entry.player_scores.map((player, pidx) => {
                              const isCounting = entry.counting_scores && entry.counting_scores.includes(player.score);
                              return (
                                <div
                                  key={pidx}
                                  className={`p-4 rounded-lg border-2 ${
                                    isCounting
                                      ? "border-[#006747] bg-[#006747]/5"
                                      : "border-[#E2E8E4] bg-white"
                                  }`}
                                >
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs font-semibold text-[#8A9A92] uppercase" style={{ fontFamily: "'Figtree', sans-serif" }}>
                                      Tier {player.tier}
                                    </span>
                                    {isCounting && (
                                      <span className="text-xs font-semibold text-[#006747] uppercase bg-[#006747]/10 px-2 py-0.5 rounded" style={{ fontFamily: "'Figtree', sans-serif" }}>
                                        Counting
                                      </span>
                                    )}
                                  </div>
                                  <p className="font-semibold text-[#1C2A21] mb-2" style={{ fontFamily: "'Figtree', sans-serif" }}>
                                    {player.name}
                                    {player.missed_cut && (
                                      <span className="ml-2 text-xs text-red-600 font-normal">(MC)</span>
                                    )}
                                  </p>
                                  {player.score !== null ? (
                                    <div className="space-y-1">
                                      <p className="text-2xl font-bold text-[#006747]" style={{ fontFamily: "'Figtree', sans-serif" }}>
                                        {player.score}
                                      </p>
                                      <div className="flex space-x-2 text-xs text-[#8A9A92]" style={{ fontFamily: "'Figtree', sans-serif" }}>
                                        <span>R1: {player.round1 || "-"}</span>
                                        <span>R2: {player.round2 || "-"}</span>
                                        <span>R3: {player.round3 || "-"}</span>
                                        <span>R4: {player.round4 || "-"}</span>
                                      </div>
                                    </div>
                                  ) : (
                                    <p className="text-[#8A9A92]" style={{ fontFamily: "'Figtree', sans-serif" }}>No score yet</p>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
};

export default Leaderboard;
